import { useEffect } from "react";
import { SomethingWentWrong } from "@/react/components/shared/ErrorPages/SomethingWentWrong";
import { withErrorBoundary } from "@/react/components/shared/ErrorPages/WithErrorBoundary";
import {
  isVersionGreaterThanOrEqual,
  useElectronAPI,
} from "@/react/hooks/utils/useElectronAPI";
import type { BackendProps } from "@/react/providers";
import { setDateFnsOptions } from "@circle-react/config/Locales/DateFns";
import { setFlatpickrLocale } from "@circle-react/config/Locales/Flatpickr";
import { initializeBugsnag } from "@circle-react/helpers/bugsnagHelpers";
import { MainAppProviders } from "@circle-react/providers";
import { MultiContextProvider } from "@circle-react/providers/MultiContextProvider";
import { applyV2Colors } from "@circle-react/providers/Theme";
import { HighlightStylesByAppearance } from "@circle-react-shared/HighlightJs/HighlightStylesByAppearance";
import { HEADLESS_UI_POPOVER_ROOT } from "@circle-react-uikit/PopoverPortal";
import { MainAppContent } from "../components/MainAppContent";
import { ElectronAppWrapper } from "../components/MainAppContent/ElectronAppWrapper";

initializeBugsnag();

export type MainAppProps = BackendProps;

// This component is mapped at app/javascript/controllers/react_mount_controller.js
// And rendered at app/views/shared/_react_app.html.erb
const MainApp = ({
  notice,
  alert,
  display_community_switcher = false,
  display_header = true,
  minimal_layout = false,
  show_widget_bot = false,
  support_widget_v2_enabled = false,
  ...rest
}: MainAppProps) => {
  const { isElectron, electronVersion } = useElectronAPI();
  const shouldUseElectronWrapper =
    isElectron && isVersionGreaterThanOrEqual(String(electronVersion), "0.1.2");

  useEffect(() => {
    applyV2Colors();
    void setDateFnsOptions();
    void setFlatpickrLocale();
  }, []);

  const contentProps = {
    notice,
    alert,
    shouldDisplayHeader: display_header,
    shouldDisplayCommunitySwitcher: display_community_switcher,
    hasMinimalLayout: minimal_layout,
    showWidgetBot: show_widget_bot,
    supportWidgetV2Enabled: support_widget_v2_enabled,
  };

  const ContentComponent = shouldUseElectronWrapper
    ? ElectronAppWrapper
    : MainAppContent;

  const backendProps: BackendProps = {
    notice,
    alert,
    display_community_switcher,
    display_header,
    minimal_layout,
    show_widget_bot,
    support_widget_v2_enabled,
    ...rest,
  };

  return (
    <MainAppProviders backendProps={backendProps}>
      <HighlightStylesByAppearance />
      <div
        id={HEADLESS_UI_POPOVER_ROOT}
        className="bg-secondary"
        data-testid="main-app-provider"
      >
        <MultiContextProvider>
          <ContentComponent {...contentProps} />
        </MultiContextProvider>
      </div>
    </MainAppProviders>
  );
};

export const App = withErrorBoundary(MainApp, SomethingWentWrong);
